import { ExtRegionParams } from '@/types/workbench';
import { FC, useEffect, useRef } from 'react';
import styles from './videoplayer.module.scss';

/** Parse display number from fragment name like "Topic 14" or "Clip 3", else use 1-based index */
function getDisplayNumber(fragmentName: string | undefined, index: number): number {
  if (!fragmentName) return index + 1;
  const match = fragmentName.match(/(?:Topic|Segment|Clip)\s*(\d+)/i);
  return match ? parseInt(match[1], 10) : index + 1;
}

// Distinct colors for clip/segment blocks (no orange - user wants only segment colors, no orange)
const LANE_COLORS = [
  '#35c176', // green
  '#8b5cf6', // purple
  '#3b82f6', // blue
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#0ea5e9', // sky
  '#14b8a6', // teal
  '#6366f1', // indigo
];

const SegmentsClipsLane: FC<{
  items: ExtRegionParams[];
  durationSec: number;
  zoomLevel: number;
  waveformScrollContainer: HTMLElement | null;
  onBlockClick?(id: string): void;
  onBackgroundClick?(): void;
}> = ({
  items,
  durationSec,
  zoomLevel,
  waveformScrollContainer,
  onBlockClick,
  onBackgroundClick,
}) => {
  const laneScrollRef = useRef<HTMLDivElement>(null);

  // Use the waveform's actual content width so the lane scrolls in lockstep
  // with the waveform. Falls back to durationSec * zoomLevel before mount.
  const waveformContentWidth = waveformScrollContainer?.scrollWidth ?? 0;
  const totalWidth =
    waveformContentWidth > 0 ? waveformContentWidth : Math.max(durationSec * zoomLevel, 1000);

  // Sync scroll between lane and waveform (when waveform is mounted)
  useEffect(() => {
    const laneEl = laneScrollRef.current;
    const waveformEl = waveformScrollContainer;
    if (!laneEl || !waveformEl || !(waveformEl instanceof HTMLElement)) return;

    let isSyncingFromLane = false;
    let isSyncingFromWaveform = false;

    const syncFromLane = (): void => {
      if (isSyncingFromWaveform) return;
      isSyncingFromLane = true;
      waveformEl.scrollLeft = laneEl.scrollLeft;
      requestAnimationFrame(() => {
        isSyncingFromLane = false;
      });
    };

    const syncFromWaveform = (): void => {
      if (isSyncingFromLane) return;
      isSyncingFromWaveform = true;
      laneEl.scrollLeft = waveformEl.scrollLeft;
      requestAnimationFrame(() => {
        isSyncingFromWaveform = false;
      });
    };

    laneEl.addEventListener('scroll', syncFromLane, { passive: true });
    waveformEl.addEventListener('scroll', syncFromWaveform, { passive: true });

    return () => {
      laneEl.removeEventListener('scroll', syncFromLane);
      waveformEl.removeEventListener('scroll', syncFromWaveform);
    };
  }, [waveformScrollContainer]);

  const handleLaneClick = (e: React.MouseEvent): void => {
    if ((e.target as HTMLElement).closest?.('button')) return;
    onBackgroundClick?.();
  };

  if (items.length === 0) {
    return (
      <div className={styles.segmentsClipsLane} ref={laneScrollRef}>
        <div
          className={styles.segmentsClipsLaneInner}
          style={{ width: totalWidth }}
          onClick={onBackgroundClick}
        />
      </div>
    );
  }

  return (
    <div className={styles.segmentsClipsLane} ref={laneScrollRef}>
      <div
        className={styles.segmentsClipsLaneInner}
        style={{ width: totalWidth }}
        onClick={handleLaneClick}
        role="presentation"
      >
        {items.map((item, index) => {
          const end = item.end ?? item.start;
          const duration = end - item.start;

          // No meaningful duration: don't render
          if (duration <= 0) return null;

          // Position blocks as percentages of durationSec — the same
          // formula WaveSurfer uses for region left/right percentages.
          // The inner div width matches the waveform's actual scrollWidth,
          // so percentage positions align exactly with WaveSurfer regions
          // regardless of zoom level or fit-to-screen rounding.
          const leftPct = durationSec > 0 ? (item.start / durationSec) * 100 : 0;
          const widthPct = durationSec > 0 ? (duration / durationSec) * 100 : 0;

          const color = LANE_COLORS[index % LANE_COLORS.length];
          const displayName = item.fragment?.fragmentName || `Item ${index + 1}`;
          const seqNum = getDisplayNumber(item.fragment?.fragmentName, index);

          return (
            <button
              key={item.id}
              type="button"
              className={styles.segmentClipBlock}
              style={{
                left: `${leftPct}%`,
                width: `${Math.max(widthPct, 0.3)}%`,
                backgroundColor: color,
              }}
              onClick={() => item.id && onBlockClick?.(item.id)}
              title={`${displayName} (${item.start.toFixed(1)}s - ${end.toFixed(1)}s)`}
            >
              <span className={styles.segmentClipBlockNum}>{seqNum}</span>
              <span className={styles.segmentClipBlockName}>{displayName}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SegmentsClipsLane;
